import { useMemo, useState, type ReactNode } from "react";

import {
  useGetV1People,
  type EnvelopePeopleListDataNotServedItem,
  type Figure,
  type Lens,
  type Person,
  type PersonScope,
} from "@solarkey/api-client";
import {
  EmptyState,
  ErrorState,
  Skeleton,
  StateWord,
  formatMoneyAmount,
  formatNumber,
  isWithheld,
  stateWord,
  useLocale,
} from "@solarkey/ui";

import { PageHeader } from "../shell/PageHeader";

/* ════════════════════════════════════════════════════════════════════════════
   M2 — People and access (wireframe §06, Admin / UX-001). The tenant's seat
   roster: who holds a seat, what the seat reaches, and the lenses and authority
   that seat grants.

   Every fact on this screen comes from `GET /v1/people` (Envelope_PeopleList):
   the real `user_roles` seats of the caller's tenant, each person's identity,
   and — per seat — the lenses and authority the UX-001 capability resolver
   derives from the seat's ROLE under the tenant's TIER. The same resolution
   `/v1/session/capabilities` performs, so the roster and the shell can never
   disagree about what a seat grants.

   Three disciplines the read keeps and the screen must not undo:

   * **Every seat, not one.** A person may hold several seats (different roles,
     different site narrowings). `person.scopes` carries all of them and this
     screen renders all of them; it never picks one.
   * **`access_source: "role_default"`.** Lenses and authority are a function of
     (role, tier, entitlements, currency) — there is NO per-person override
     table. The chips are therefore labelled as the role bundle's and are not
     interactive: there is nothing on this screen to toggle.
   * **Counts are real or withheld.** `total`, `tenant_site_total` and each
     seat's `site_count` are server-side COUNTs carried as Figures. They
     serialise as floats (15.0) and are formatted without decimals; a withheld
     figure prints its state word, never a rounded zero.

   What §06 shows that the platform holds no source for — organisation, the
   relationship-wide denominator, certificates, devices, identity provider, MFA
   and true last activity — arrives in `data.not_served`, each with a state word
   and a templated reason. Each keeps its place on the screen and prints that
   word: never blank, never a dash.

   `last_sign_in_at` is last SIGN-IN. It is labelled as such and does not imply
   activity since; true last activity is one of the withheld fields above.

   Build 01 serves NO people write endpoint of any kind, so Save, Add person and
   Deactivate keep their places and say why they cannot act, rather than looking
   live and doing nothing (the house pattern — see SiteNow and Reports).
   Built on @solarkey/components classes so fonts match.
   ════════════════════════════════════════════════════════════════════════════ */

/** No people write path exists in this build; each control states its own case. */
const NO_WRITE = {
  add:
    "Adding a person is not wired: the platform serves no people write endpoint " +
    "in this build, so no invitation and no seat could be created.",
  save:
    "Saving is not wired: the platform serves no people write endpoint in this " +
    "build, so a change to someone's access could not be recorded.",
  deactivate:
    "Deactivating is not wired: the platform serves no people write endpoint in " +
    "this build, so a seat could not be revoked.",
} as const;

/** Plain words for the lens codes (UX-001 §4.2) — the enum's vocabulary, not data. */
const LENS_LABEL: Record<string, string> = {
  energy: "Energy",
  equipment: "Equipment",
  value: "Value",
  contracts: "Contracts",
  work: "Work",
  documents: "Documents",
  control: "Control",
  people: "People",
};

/** Plain words for the authority ladder and reach (UX-003 §5.1, §5.2). */
const REACH_LABEL: Record<string, string> = {
  inverter_plant: "Inverter and plant",
  combiner: "Combiner",
  string: "String",
};

const AUTONOMY_LABEL: Record<string, string> = {
  observe: "Observe",
  recommend: "Recommend",
  confirm: "Confirm",
  bounded_autonomy: "Bounded autonomy",
  autonomous: "Autonomous",
};

type NotServed = EnvelopePeopleListDataNotServedItem;

export function People(): ReactNode {
  const locale = useLocale();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The search is the SERVER's filter, so `total` is a real count over exactly
  // what the page shows — the browser never derives a remainder of its own.
  const q = query.trim();
  const peopleQuery = useGetV1People(q ? { q } : undefined);
  const response = peopleQuery.data;
  const envelope = response?.status === 200 ? response.data : undefined;
  const d = envelope?.data;
  const items: Person[] = d?.items ?? [];
  const cursor = envelope?.meta?.cursor ?? null;

  /** `not_served` by field name — each §06 field with no source, with its reason. */
  const notServed = useMemo(() => {
    const map = new Map<string, NotServed>();
    for (const entry of d?.not_served ?? []) map.set(entry.field, entry);
    return map;
  }, [d]);

  // No selection yet means the first person on the served page, never a name
  // this screen holds of its own.
  const selected = items.find((p) => p.id === selectedId) ?? items[0];

  const scopeLine = d
    ? `${countText(d.total, locale)} on the roster · ` +
      `sites in this tenant: ${countText(d.tenant_site_total, locale)} · ` +
      "lenses and authority from the role bundle"
    : "Seats, lenses and authority as the platform resolves them";

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title="People"
        scope={scopeLine}
        showTimeControl={false}
        family="admin"
        action={
          <button
            type="button"
            className="btn"
            style={notWiredStyle}
            disabled
            aria-disabled="true"
            title={NO_WRITE.add}
          >
            Add person · not wired
          </button>
        }
      />

      {response?.status === 403 ? (
        <ErrorState
          title="The roster is not served to this seat"
          message={
            "People and their email addresses are personal data, so the roster is " +
            "served to an administrator seat only. This session does not hold one."
          }
        />
      ) : peopleQuery.isError ? (
        <ErrorState
          title="The roster could not be read"
          message="The people read did not answer, so no seat can be stated."
          onRetry={() => void peopleQuery.refetch()}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 14, alignItems: "start" }}>
          {/* ── The roster ── */}
          <div className="card">
            <h4>
              People
              <span className="sub">every seat each person holds</span>
              <span className="right">
                <input
                  className="input"
                  type="search"
                  placeholder="Find by name or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Find by name or email"
                  style={{ width: 220, fontSize: 12 }}
                />
              </span>
            </h4>

            {peopleQuery.isLoading ? (
              <Skeleton variant="text" lines={4} />
            ) : items.length === 0 ? (
              <EmptyState
                variant="no_data"
                title={q ? "No one on the roster matches this search" : "This tenant holds no seats"}
                message={
                  q
                    ? "The server found no person whose name or email matches, so there is nothing to show."
                    : "The platform serves no seat for this tenant, so there is no one to show."
                }
              />
            ) : (
              <>
                <table className="table" aria-label="People">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Seats (role bundle)</th>
                      <th>Site scope</th>
                      <th>Lenses (from the role bundle)</th>
                      <th>Authority (from the role bundle)</th>
                      <th>Last sign-in</th>
                      <th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => {
                      const isSel = selected?.id === p.id;
                      return (
                        <tr
                          key={p.id}
                          style={isSel ? { background: "var(--sk-color-accent-soft)" } : undefined}
                        >
                          <td>
                            <button
                              type="button"
                              style={personButtonStyle}
                              aria-pressed={isSel}
                              onClick={() => setSelectedId(p.id)}
                              title="Show this person's seats beside the roster"
                            >
                              {p.name}
                            </button>
                            <div style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>{p.email}</div>
                          </td>
                          <td>
                            <SeatLines person={p} render={(s) => <RoleName scope={s} />} />
                          </td>
                          <td>
                            <SeatLines person={p} render={(s) => <ScopeText scope={s} />} />
                          </td>
                          <td>
                            <LensChips lenses={p.lenses} />
                          </td>
                          <td>
                            <SeatLines person={p} render={(s) => <AuthoritySummary scope={s} />} />
                          </td>
                          <td>
                            <SignIn at={p.last_sign_in_at} locale={locale} />
                          </td>
                          <td>
                            {/* Last ACTIVITY, which has no source — never the sign-in above. */}
                            <NotServedWord entry={notServed.get("last_activity_at")} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p style={{ marginTop: 8, fontSize: 11, color: "var(--sk-color-ink-3)" }}>
                  {items.length} shown of <Count figure={d?.total} /> (the server&apos;s own count
                  over this filter).{" "}
                  {cursor
                    ? "The roster continues past this page; paging is served by cursor and is not wired to a control here."
                    : "This is the whole roster for the filter."}
                </p>
              </>
            )}
          </div>

          {/* ── One person's seats, lenses and authority ── */}
          <div className="card">
            {peopleQuery.isLoading ? (
              <Skeleton variant="text" lines={6} />
            ) : !selected ? (
              <EmptyState
                variant="no_data"
                title="No person to show"
                message="Nothing on the roster matches, so no seat can be described."
              />
            ) : (
              <>
                <h4>
                  {selected.name}
                  <span className="sub">
                    access resolved from the role bundle
                    {d?.access_source ? ` (${d.access_source})` : ""}
                  </span>
                  <span className="right">
                    {selected.is_active === false && <span className="chip">deactivated</span>}
                    {selected.is_locked && (
                      <span
                        className="chip"
                        title={selected.locked_until ? `Locked until ${selected.locked_until}` : undefined}
                      >
                        locked
                      </span>
                    )}
                  </span>
                </h4>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {/* Nothing links a person to an organisation, so the field says so
                      instead of naming one (CLAUDE.md rule 5 besides). */}
                  <Field
                    label="Organisation"
                    value={<NotServedWord entry={notServed.get("organisation")} />}
                  />
                  <Field label="Email" value={selected.email} />

                  <Field
                    label="Seats held"
                    value={
                      <SeatLines person={selected} render={(s) => <RoleName scope={s} />} />
                    }
                  />
                  <Field
                    label="Site scope per seat"
                    value={
                      <SeatLines person={selected} render={(s) => <ScopeText scope={s} />} />
                    }
                  />

                  {/* The "N of M" the wireframe shows needs a relationship-wide
                      denominator, which has no source. The tenant's own total is
                      served and shown; the relationship one says why it cannot be. */}
                  <Field
                    label="Sites in this tenant"
                    value={<Count figure={d?.tenant_site_total} />}
                  />
                  <Field
                    label="Scope within the relationship"
                    value={<NotServedWord entry={notServed.get("relationship_site_total")} />}
                  />

                  {/* Role-derived, so it is a stated value and not a control: a
                      `label` element here would have nothing to label. */}
                  <Field
                    label="Lenses (from the role bundle)"
                    value={<LensChips lenses={selected.lenses} />}
                  />
                  <Field
                    label="Certificates on record"
                    value={<NotServedWord entry={notServed.get("certificates")} />}
                  />

                  <Field label="Devices" value={<NotServedWord entry={notServed.get("devices")} />} />
                  <Field
                    label="Identity provider"
                    value={<NotServedWord entry={notServed.get("identity_provider")} />}
                  />

                  <Field
                    label="Multi-factor authentication"
                    value={<NotServedWord entry={notServed.get("mfa")} />}
                  />
                  <Field
                    label="Last sign-in"
                    value={<SignIn at={selected.last_sign_in_at} locale={locale} />}
                  />

                  <Field
                    label="Last activity"
                    value={<NotServedWord entry={notServed.get("last_activity_at")} />}
                  />
                </div>

                {/* What the role bundle grants, seat by seat — the resolver's own
                    output, not a per-person grant and not a paragraph about one. */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    What each seat grants
                  </div>
                  <div style={{ fontSize: 11, color: "var(--sk-color-ink-2)", lineHeight: 1.5 }}>
                    Resolved from the seat&apos;s role under this tenant&apos;s tier. There is no
                    per-person override in this build, so nothing below can be set for one person.
                  </div>
                  {selected.scopes.map((s) => (
                    <div key={s.seat_id} style={{ marginTop: 8, fontSize: 11 }}>
                      <div style={{ fontWeight: 600, color: "var(--sk-color-ink-1)" }}>
                        <RoleName scope={s} /> · <ScopeText scope={s} />
                      </div>
                      <div style={{ color: "var(--sk-color-ink-2)", lineHeight: 1.5 }}>
                        <LensChips lenses={s.lenses} />
                      </div>
                      <AuthorityDetail scope={s} locale={locale} />
                    </div>
                  ))}
                </div>

                {/* No people write endpoint exists, so these keep their place and
                    say why they cannot act (the house pattern). */}
                <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
                  <button
                    type="button"
                    className="btn"
                    style={notWiredStyle}
                    disabled
                    aria-disabled="true"
                    title={NO_WRITE.save}
                  >
                    Save · not wired
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={notWiredStyle}
                    disabled
                    aria-disabled="true"
                    title={NO_WRITE.deactivate}
                  >
                    Deactivate · not wired
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Seats ───────────────────────────────────────────────────────────────────
   A person may hold several seats and the endpoint returns all of them, so every
   seat is rendered. One line per seat, never the first seat alone. */
function SeatLines(
  { person, render }: { person: Person; render: (scope: PersonScope) => ReactNode },
): ReactNode {
  if (person.scopes.length === 0) {
    return <span style={{ color: "var(--sk-color-ink-3)" }}>No seat on this roster</span>;
  }
  return (
    <>
      {person.scopes.map((s) => (
        <div key={s.seat_id}>{render(s)}</div>
      ))}
    </>
  );
}

/** The seat's role slug, or the served reason why it could not be read. */
function RoleName({ scope }: { scope: PersonScope }): ReactNode {
  if (!scope.role) {
    return <StateWord state="CANNOT_CHECK" reason={scope.reason} />;
  }
  return <>{scope.role}</>;
}

/** Site IDENTIFIERS only — never a site or customer name, never a region. */
function ScopeText({ scope }: { scope: PersonScope }): ReactNode {
  if (scope.tenant_wide) {
    return (
      <>
        Tenant-wide, reaching <Count figure={scope.site_count} /> of this tenant&apos;s sites
      </>
    );
  }
  return (
    <>
      {scope.site_ids.join(", ")} (<Count figure={scope.site_count} />)
    </>
  );
}

/* ── Lenses ──────────────────────────────────────────────────────────────────
   The role bundle's lenses, resolved under the tenant tier. Plain spans: there
   is no per-person override table, so there is nothing here to toggle. */
function LensChips({ lenses }: { lenses?: Lens[] }): ReactNode {
  if (!lenses || lenses.length === 0) {
    return <span style={{ color: "var(--sk-color-ink-3)" }}>No lens on this seat</span>;
  }
  return (
    <span
      style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}
      title="Resolved from the role bundle under this tenant's tier, not granted per person"
    >
      {lenses.map((l) => (
        <span key={l} className="chip on">
          {LENS_LABEL[l] ?? l}
        </span>
      ))}
    </span>
  );
}

/* ── Authority ───────────────────────────────────────────────────────────────
   Also resolved from the role under the tier. Withheld when the seat's role
   could not be read, with the served reason. */
function AuthoritySummary({ scope }: { scope: PersonScope }): ReactNode {
  const a = scope.authority;
  if (!a) return <StateWord state="CANNOT_CHECK" reason={scope.reason} />;
  const parts = [
    a.autonomy ? (AUTONOMY_LABEL[a.autonomy] ?? a.autonomy) : null,
    a.reach ? (REACH_LABEL[a.reach] ?? a.reach) : null,
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return <StateWord state="NOT_MEASURED" reason={scope.reason} />;
  return <>{parts.join(" · ")}</>;
}

function AuthorityDetail({ scope, locale }: { scope: PersonScope; locale: string }): ReactNode {
  const a = scope.authority;
  if (!a) {
    return (
      <div style={{ color: "var(--sk-color-ink-2)" }}>
        Authority: <StateWord state="CANNOT_CHECK" reason={scope.reason} />
      </div>
    );
  }
  const limits = a.limits ?? [];
  return (
    <div style={{ color: "var(--sk-color-ink-2)", lineHeight: 1.5 }}>
      <div>
        Autonomy: {a.autonomy ? (AUTONOMY_LABEL[a.autonomy] ?? a.autonomy) : <StateWord state="NOT_MEASURED" />}
        {" · "}
        Reach: {a.reach ? (REACH_LABEL[a.reach] ?? a.reach) : <StateWord state="NOT_MEASURED" />}
      </div>
      <div>
        Second approver above the site threshold: {a.four_eyes === undefined
          ? <StateWord state="NOT_MEASURED" />
          : a.four_eyes ? "required" : "not required"}
        {" · "}
        May grant access to other organisations: {a.can_grant === undefined
          ? <StateWord state="NOT_MEASURED" />
          : a.can_grant ? "yes" : "no"}
      </div>
      <div>
        Approval limits:{" "}
        {limits.length === 0 ? (
          <StateWord state="NOT_MEASURED" reason={scope.reason} />
        ) : (
          limits
            .map((l) =>
              [
                l.action ?? "",
                l.max_amount === null || l.max_amount === undefined
                  ? "unlimited within the tier"
                  : l.currency
                    ? formatMoneyAmount(l.max_amount, l.currency, locale)
                    : formatNumber(l.max_amount, locale, { maximumFractionDigits: 0 }),
              ]
                .filter((part) => part !== "")
                .join(" "),
            )
            .join(" · ")
        )}
      </div>
    </div>
  );
}

/* ── Sign-in ─────────────────────────────────────────────────────────────────
   `last_sign_in_at` is last SIGN-IN. Null means the platform recorded no
   sign-in for this person; it is said that way, never as a dash and never as an
   invented "today". True last ACTIVITY is a withheld field of its own. */
function SignIn({ at, locale }: { at?: string | null; locale: string }): ReactNode {
  if (!at) {
    return (
      <span
        style={{ color: "var(--sk-color-ink-3)" }}
        title="The identity store holds no sign-in for this person."
      >
        No sign-in recorded
      </span>
    );
  }
  const parsed = new Date(at);
  const text = Number.isNaN(parsed.getTime()) ? at : parsed.toLocaleString(locale);
  return <span title={`Last sign-in ${at}. It does not imply activity since.`}>{text}</span>;
}

/* ── Withheld §06 fields ─────────────────────────────────────────────────────
   One of the seven fields `data.not_served` names. The state word carries the
   server's templated reason; the field keeps its place on the screen. */
function NotServedWord({ entry }: { entry?: NotServed }): ReactNode {
  if (!entry) return <StateWord state="CANNOT_CHECK" />;
  return <StateWord state={entry.state} reason={entry.reason} />;
}

/* ── Counts ──────────────────────────────────────────────────────────────────
   A served count is a real Figure that serialises as a float (15.0), so it is
   formatted without decimals. A withheld one prints its state word — a count
   nobody established is never rounded into a number. The figure's unit is the
   word "count", so it is not printed beside the number. */
function Count({ figure }: { figure?: Figure }): ReactNode {
  const locale = useLocale();
  if (!figure) return <StateWord state="CANNOT_CHECK" />;
  if (isWithheld(figure)) return <StateWord state={figure.state} reason={figure.reason} />;
  return <>{formatNumber(figure.value as number, locale, { maximumFractionDigits: 0 })}</>;
}

/** The same rule as `Count`, where only a string can go (the header scope line). */
function countText(figure: Figure | undefined, locale: string): string {
  if (!figure || isWithheld(figure)) return stateWord(figure?.state ?? "CANNOT_CHECK");
  return formatNumber(figure.value as number, locale, { maximumFractionDigits: 0 });
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="field">
      <label>{label}</label>
      <div
        className="in ro"
        style={{ fontSize: 12, height: "auto", minHeight: 30, paddingBlock: 6, lineHeight: 1.35 }}
      >
        {value}
      </div>
    </div>
  );
}

/** A control that keeps its place and cannot act (the house pattern). */
const notWiredStyle: React.CSSProperties = {
  opacity: 0.55,
  cursor: "not-allowed",
};

/** The person cell doubles as the selector; it keeps the cell's own type. */
const personButtonStyle: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  fontWeight: 600,
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
};
